const getSections = (page) => Array.from(page.querySelector('.page-content')?.children || [])
  .filter((child) => child.hasAttribute('data-rdo-section'))

const safeBottom = (page) => {
  const pageHeight = page.getBoundingClientRect().height
  return page.querySelector('.page-footer').getBoundingClientRect().top - (pageHeight / 297) * 8
}

const fitsOnPage = (page) => {
  const lastSection = getSections(page).at(-1)
  return !lastSection || lastSection.getBoundingClientRect().bottom <= safeBottom(page)
}

const moveTableRows = (sourceSection, targetPage) => {
  const targetSections = getSections(targetPage)
  const lastSection = targetSections.at(-1)
  const sameTable = lastSection?.dataset.rdoSection === sourceSection.dataset.rdoSection
  const targetSection = sameTable ? lastSection : sourceSection.cloneNode(true)
  const targetRows = targetSection.querySelector('tbody')
  const sourceRows = sourceSection.querySelector('tbody')

  if (!sameTable) {
    targetRows.replaceChildren()
    targetPage.querySelector('.page-footer').before(targetSection)
  }

  let moved = 0
  while (sourceRows.firstElementChild) {
    const row = sourceRows.firstElementChild
    targetRows.append(row)
    if (!fitsOnPage(targetPage)) {
      sourceRows.prepend(row)
      break
    }
    moved += 1
  }

  if (!moved && !sameTable) targetSection.remove()
  if (!sourceRows.children.length) sourceSection.remove()
  return moved > 0
}

const moveObservationLines = (sourceSection, targetPage) => {
  const lastSection = getSections(targetPage).at(-1)
  const sameSection = lastSection?.dataset.rdoSection === 'observation'
  const targetSection = sameSection ? lastSection : sourceSection.cloneNode(true)
  const targetText = targetSection.querySelector('p')
  const sourceText = sourceSection.querySelector('p')
  const lines = sourceText.innerHTML.split(/<br\s*\/?\s*>/i)

  if (!sameSection) {
    targetText.replaceChildren()
    targetPage.querySelector('.page-footer').before(targetSection)
  }

  const existingLines = sameSection ? targetText.innerHTML.split(/<br\s*\/?\s*>/i) : []
  const fittedLines = [...existingLines]
  let moved = 0
  for (const line of lines) {
    fittedLines.push(line)
    targetText.innerHTML = fittedLines.join('<br />')
    if (!fitsOnPage(targetPage)) {
      fittedLines.pop()
      targetText.innerHTML = fittedLines.join('<br />')
      break
    }
    moved += 1
  }

  if (!moved && !sameSection) targetSection.remove()
  if (moved === lines.length) sourceSection.remove()
  else if (moved) sourceText.innerHTML = lines.slice(moved).join('<br />')
  return moved > 0
}

const moveSection = (sourceSection, sourcePage, targetPage) => {
  if (sourceSection.matches('.table-section')) return moveTableRows(sourceSection, targetPage)
  if (sourceSection.dataset.rdoSection === 'observation') return moveObservationLines(sourceSection, targetPage)

  const nextSibling = sourceSection.nextSibling
  targetPage.querySelector('.page-footer').before(sourceSection)
  if (fitsOnPage(targetPage)) return true

  sourcePage.querySelector('.page-content').insertBefore(sourceSection, nextSibling)
  return false
}

const fitRdoDetails = (document) => {
  document.querySelectorAll('[data-rdo-first-page]').forEach((firstPage) => {
    const detailPages = []
    let page = firstPage
    while (page?.classList.contains('rdo-page') && !page.hasAttribute('data-rdo-photo-page') && (page === firstPage || !page.hasAttribute('data-rdo-first-page'))) {
      detailPages.push(page)
      page = page.nextElementSibling
    }

    for (let index = 0; index < detailPages.length - 1; index += 1) {
      const targetPage = detailPages[index]
      const sourcePage = detailPages[index + 1]
      let sourceSection = getSections(sourcePage)[0]

      while (sourceSection && moveSection(sourceSection, sourcePage, targetPage)) {
        sourceSection = getSections(sourcePage)[0]
      }

      if (!getSections(sourcePage).length) {
        sourcePage.remove()
        detailPages.splice(index + 1, 1)
        index -= 1
      }
    }

    detailPages.forEach((detailPage) => detailPage.removeAttribute('data-rdo-last-detail'))
    detailPages.at(-1)?.setAttribute('data-rdo-last-detail', '')
  })
}

// Ajusta as seções e fotos usando a altura renderizada e a área livre acima do rodapé.
// O cartão mantém a largura de uma coluna, inclusive para fotos verticais.
export const fitRdoPhotos = (printWindow) => {
  const document = printWindow.document
  fitRdoDetails(document)
  const lastDetailPages = document.querySelectorAll('[data-rdo-last-detail]')

  lastDetailPages.forEach((detailPage) => {
    const photoPages = []
    let nextPage = detailPage.nextElementSibling

    while (nextPage?.hasAttribute('data-rdo-photo-page')) {
      photoPages.push(nextPage)
      nextPage = nextPage.nextElementSibling
    }

    if (!photoPages.length) return

    const cards = photoPages.flatMap((page) => Array.from(page.querySelectorAll('.photo-card')))
    const section = document.createElement('section')
    section.className = 'photos-section photos-section--inline'
    section.innerHTML = '<h2>Fotos</h2><div class="photos-grid photos-grid--inline"></div>'

    const footer = detailPage.querySelector('.page-footer')
    footer.before(section)
    const grid = section.querySelector('.photos-grid')
    const availableBottom = safeBottom(detailPage)
    let inlineCount = 0

    while (inlineCount < Math.min(4, cards.length)) {
      grid.append(cards[inlineCount])
      grid.className = `photos-grid photos-grid--inline photos-grid--${inlineCount + 1}`

      if (section.getBoundingClientRect().bottom > availableBottom) {
        cards[inlineCount].remove()
        grid.className = `photos-grid photos-grid--inline photos-grid--${inlineCount}`
        break
      }

      inlineCount += 1
    }

    if (!inlineCount) section.remove()

    const remainingCards = cards.slice(inlineCount)
    const remainingPageCount = Math.ceil(remainingCards.length / 4)

    photoPages.forEach((page, index) => {
      if (index >= remainingPageCount) {
        page.remove()
        return
      }

      const pageCards = remainingCards.slice(index * 4, (index + 1) * 4)
      const pageGrid = page.querySelector('.photos-grid')
      pageGrid.className = `photos-grid photos-grid--${pageCards.length}`
      pageGrid.replaceChildren(...pageCards)
      page.querySelector('.photos-section h2').textContent = inlineCount || index ? 'Fotos - continuação' : 'Fotos'
    })
  })

  // Cada RDO tem sua própria numeração, inclusive na impressão de vários RDOs.
  document.querySelectorAll('[data-rdo-first-page]').forEach((firstPage) => {
    const pages = []
    let page = firstPage

    while (page?.classList.contains('rdo-page') && (page === firstPage || !page.hasAttribute('data-rdo-first-page'))) {
      pages.push(page)
      page = page.nextElementSibling
    }

    pages.forEach((item, index) => {
      item.querySelector('.page-footer').textContent = `Página ${index + 1} de ${pages.length}`
    })
  })
}
